/**
 * Berechtigungs-Bits (BigInt). Die Bitpositionen entsprechen bewusst Discord, damit die Brücke
 * Rollen und Overrides 1:1 übernehmen kann. Lagerfeuer-eigene Bits liegen ab Bit 56.
 * Über die Leitung (REST/Socket/JSON) werden Bitfelder IMMER als Dezimal-String übertragen.
 */
export const Permissions = {
  CREATE_INVITE: 1n << 0n,
  KICK_MEMBERS: 1n << 1n,
  BAN_MEMBERS: 1n << 2n,
  ADMINISTRATOR: 1n << 3n,
  MANAGE_CHANNELS: 1n << 4n,
  MANAGE_SERVER: 1n << 5n,
  ADD_REACTIONS: 1n << 6n,
  VIEW_AUDIT_LOG: 1n << 7n,
  PRIORITY_SPEAKER: 1n << 8n,
  /** Bildschirm übertragen (Discord: STREAM). */
  STREAM: 1n << 9n,
  VIEW_CHANNEL: 1n << 10n,
  SEND_MESSAGES: 1n << 11n,
  MANAGE_MESSAGES: 1n << 13n,
  EMBED_LINKS: 1n << 14n,
  ATTACH_FILES: 1n << 15n,
  READ_MESSAGE_HISTORY: 1n << 16n,
  MENTION_EVERYONE: 1n << 17n,
  USE_EXTERNAL_EMOJIS: 1n << 18n,
  CONNECT: 1n << 20n,
  SPEAK: 1n << 21n,
  MUTE_MEMBERS: 1n << 22n,
  DEAFEN_MEMBERS: 1n << 23n,
  MOVE_MEMBERS: 1n << 24n,
  USE_VAD: 1n << 25n,
  CHANGE_NICKNAME: 1n << 26n,
  MANAGE_NICKNAMES: 1n << 27n,
  MANAGE_ROLES: 1n << 28n,
  MANAGE_WEBHOOKS: 1n << 29n,
  /** Emojis, Sticker, Sounds verwalten (Discord: MANAGE_GUILD_EXPRESSIONS). */
  MANAGE_EMOJIS: 1n << 30n,
  MANAGE_THREADS: 1n << 34n,
  /** Öffentliche Threads und Forenbeiträge erstellen. */
  CREATE_THREADS: 1n << 35n,
  CREATE_PRIVATE_THREADS: 1n << 36n,
  USE_EXTERNAL_STICKERS: 1n << 37n,
  SEND_IN_THREADS: 1n << 38n,
  /** Mitglieder in Timeout setzen. */
  MODERATE_MEMBERS: 1n << 40n,
  USE_SOUNDBOARD: 1n << 42n,
  USE_EXTERNAL_SOUNDS: 1n << 45n,
  PIN_MESSAGES: 1n << 51n,
  // --- Lagerfeuer-eigene Bits ---
  /** Kamera einschalten (Discord bündelt das unter STREAM). */
  VIDEO: 1n << 56n,
  /** Super-Reaktionen vergeben. */
  USE_SUPER_REACTIONS: 1n << 57n,
} as const;

export type PermissionName = keyof typeof Permissions;

export const ALL_PERMISSIONS: bigint = Object.values(Permissions).reduce((a, b) => a | b, 0n);

/** Rechte der @everyone-Rolle eines neuen Servers. */
export const DEFAULT_EVERYONE_PERMISSIONS: bigint =
  Permissions.CREATE_INVITE |
  Permissions.ADD_REACTIONS |
  Permissions.STREAM |
  Permissions.VIDEO |
  Permissions.VIEW_CHANNEL |
  Permissions.SEND_MESSAGES |
  Permissions.EMBED_LINKS |
  Permissions.ATTACH_FILES |
  Permissions.READ_MESSAGE_HISTORY |
  Permissions.USE_EXTERNAL_EMOJIS |
  Permissions.USE_EXTERNAL_STICKERS |
  Permissions.USE_EXTERNAL_SOUNDS |
  Permissions.CONNECT |
  Permissions.SPEAK |
  Permissions.USE_VAD |
  Permissions.CHANGE_NICKNAME |
  Permissions.CREATE_THREADS |
  Permissions.SEND_IN_THREADS |
  Permissions.USE_SOUNDBOARD |
  Permissions.USE_SUPER_REACTIONS;

/** Feste Rechte jedes Teilnehmers in DMs/Gruppen-DMs (keine Rollen, keine Overrides). */
export const DM_PERMISSIONS: bigint =
  Permissions.VIEW_CHANNEL |
  Permissions.SEND_MESSAGES |
  Permissions.EMBED_LINKS |
  Permissions.ATTACH_FILES |
  Permissions.READ_MESSAGE_HISTORY |
  Permissions.ADD_REACTIONS |
  Permissions.USE_EXTERNAL_EMOJIS |
  Permissions.USE_EXTERNAL_STICKERS |
  Permissions.USE_SUPER_REACTIONS |
  Permissions.CONNECT |
  Permissions.SPEAK |
  Permissions.USE_VAD |
  Permissions.VIDEO |
  Permissions.STREAM |
  Permissions.PIN_MESSAGES |
  Permissions.CREATE_THREADS |
  Permissions.SEND_IN_THREADS;

/** Rechte, die im Timeout erhalten bleiben (alles andere wird entzogen). */
export const TIMEOUT_ALLOWED: bigint = Permissions.VIEW_CHANNEL | Permissions.READ_MESSAGE_HISTORY;

/** Rechte, die ohne SEND_MESSAGES implizit entfallen (Textkanäle). */
const IMPLICIT_SEND_DEPENDENT: bigint =
  Permissions.MENTION_EVERYONE | Permissions.ATTACH_FILES | Permissions.EMBED_LINKS;

/** Rechte, die ohne CONNECT implizit entfallen (Sprachkanäle). */
const IMPLICIT_CONNECT_DEPENDENT: bigint =
  Permissions.SPEAK |
  Permissions.VIDEO |
  Permissions.STREAM |
  Permissions.USE_VAD |
  Permissions.PRIORITY_SPEAKER |
  Permissions.USE_SOUNDBOARD |
  Permissions.MUTE_MEMBERS |
  Permissions.DEAFEN_MEMBERS |
  Permissions.MOVE_MEMBERS;

export type BitfieldInput = bigint | string | number;

export function toBitfield(v: BitfieldInput | null | undefined): bigint {
  if (v === null || v === undefined || v === '') return 0n;
  return typeof v === 'bigint' ? v : BigInt(v);
}

export function serializeBitfield(v: bigint): string {
  return v.toString();
}

/**
 * true, wenn `perms` ALLE Bits aus `required` enthält. ADMINISTRATOR schließt alles ein.
 */
export function hasPermission(perms: BitfieldInput, required: BitfieldInput): boolean {
  const p = toBitfield(perms);
  const r = toBitfield(required);
  if ((p & Permissions.ADMINISTRATOR) === Permissions.ADMINISTRATOR) return true;
  return (p & r) === r;
}

export function hasAnyPermission(perms: BitfieldInput, anyOf: BitfieldInput): boolean {
  const p = toBitfield(perms);
  if ((p & Permissions.ADMINISTRATOR) === Permissions.ADMINISTRATOR) return true;
  return (p & toBitfield(anyOf)) !== 0n;
}

export function permissionNames(perms: BitfieldInput): PermissionName[] {
  const p = toBitfield(perms);
  return (Object.keys(Permissions) as PermissionName[]).filter((k) => (p & Permissions[k]) === Permissions[k]);
}

export function permissionsFromNames(names: readonly PermissionName[]): bigint {
  return names.reduce((acc, n) => acc | Permissions[n], 0n);
}

export interface RoleLike {
  id: string;
  permissions: BitfieldInput;
}

export type OverwriteTargetType = 'ROLE' | 'MEMBER';

export interface OverwriteLike {
  targetType: OverwriteTargetType;
  targetId: string;
  allow: BitfieldInput;
  deny: BitfieldInput;
}

export interface PermissionContext {
  /** ID der @everyone-Rolle (= Server-ID). */
  everyoneRoleId: string;
  /** User-ID des Mitglieds (für Mitglieds-Overrides). */
  memberId: string;
  /** Mitglied im Timeout → nur VIEW_CHANNEL/READ_MESSAGE_HISTORY. */
  timedOut?: boolean;
  /** Kanalart: steuert implizite Abhängigkeiten (Text vs. Sprache). */
  channelKind?: 'text' | 'voice';
}

/**
 * Basisrechte auf Serverebene: @everyone | alle Rollen des Mitglieds.
 * `baseRoles` MUSS die @everyone-Rolle enthalten.
 */
export function computeBasePermissions(baseRoles: readonly RoleLike[], isOwner: boolean): bigint {
  if (isOwner) return ALL_PERMISSIONS;
  let perms = 0n;
  for (const r of baseRoles) perms |= toBitfield(r.permissions);
  if ((perms & Permissions.ADMINISTRATOR) === Permissions.ADMINISTRATOR) return ALL_PERMISSIONS;
  return perms;
}

/**
 * Effektive Rechte eines Mitglieds in einem Kanal (Discord-Algorithmus):
 *  1. Owner oder ADMINISTRATOR → alles.
 *  2. Basis = @everyone | Rollen.
 *  3. Override @everyone (deny, dann allow).
 *  4. Rollen-Overrides gesammelt (alle deny, dann alle allow) – nur für Rollen des Mitglieds.
 *  5. Mitglieds-Override (deny, dann allow).
 *  6. Implizit: ohne VIEW_CHANNEL → 0; ohne SEND_MESSAGES entfallen Anhänge/Links/@everyone;
 *     ohne CONNECT entfallen Sprachrechte; Timeout → nur Lesen.
 *
 * `overrides = null` liefert die Serverrechte (ohne Kanal). Für Threads werden die Overrides des
 * Elternkanals übergeben (siehe `threadPermissions`).
 */
export function computePermissions(
  baseRoles: readonly RoleLike[],
  overrides: readonly OverwriteLike[] | null,
  isOwner: boolean,
  ctx: PermissionContext,
): bigint {
  if (isOwner || hasAdmin(baseRoles)) return ALL_PERMISSIONS;

  let perms = computeBasePermissions(baseRoles, false);
  if (overrides && overrides.length > 0) {
    const roleIds = new Set(baseRoles.map((r) => r.id));
    const everyone = overrides.find((o) => o.targetType === 'ROLE' && o.targetId === ctx.everyoneRoleId);
    if (everyone) {
      perms &= ~toBitfield(everyone.deny);
      perms |= toBitfield(everyone.allow);
    }
    let roleAllow = 0n;
    let roleDeny = 0n;
    for (const o of overrides) {
      if (o.targetType !== 'ROLE' || o.targetId === ctx.everyoneRoleId || !roleIds.has(o.targetId)) continue;
      roleAllow |= toBitfield(o.allow);
      roleDeny |= toBitfield(o.deny);
    }
    perms &= ~roleDeny;
    perms |= roleAllow;
    const member = overrides.find((o) => o.targetType === 'MEMBER' && o.targetId === ctx.memberId);
    if (member) {
      perms &= ~toBitfield(member.deny);
      perms |= toBitfield(member.allow);
    }
    // ADMINISTRATOR wirkt nur über Rollen/Owner – ein Kanal-Override darf ihn nicht verleihen (Security M6).
    perms &= ~Permissions.ADMINISTRATOR;
  }

  return applyImplicit(perms, ctx);
}

function hasAdmin(roles: readonly RoleLike[]): boolean {
  return roles.some((r) => (toBitfield(r.permissions) & Permissions.ADMINISTRATOR) !== 0n);
}

function applyImplicit(input: bigint, ctx: PermissionContext): bigint {
  let perms = input;
  if (ctx.timedOut) perms &= TIMEOUT_ALLOWED;
  if ((perms & Permissions.VIEW_CHANNEL) === 0n) return 0n;
  if (ctx.channelKind !== 'voice' && (perms & Permissions.SEND_MESSAGES) === 0n) {
    perms &= ~IMPLICIT_SEND_DEPENDENT;
  }
  if (ctx.channelKind === 'voice' && (perms & Permissions.CONNECT) === 0n) {
    perms &= ~IMPLICIT_CONNECT_DEPENDENT;
  }
  return perms;
}

/**
 * Rechte in einem Thread aus den Rechten im Elternkanal: Schreiben im Thread hängt an
 * SEND_IN_THREADS (nicht an SEND_MESSAGES); gesperrte Threads nur mit MANAGE_THREADS.
 */
export function threadPermissions(parentPerms: bigint, opts: { locked?: boolean; archived?: boolean } = {}): bigint {
  if (parentPerms === ALL_PERMISSIONS) return ALL_PERMISSIONS;
  let perms = parentPerms & ~Permissions.SEND_MESSAGES;
  const canSend = (parentPerms & Permissions.SEND_IN_THREADS) !== 0n;
  const isMod = (parentPerms & Permissions.MANAGE_THREADS) !== 0n;
  if (canSend && (!(opts.locked || opts.archived) || isMod)) perms |= Permissions.SEND_MESSAGES;
  return perms;
}
